import { useEffect } from 'react'
import { path } from 'ramda'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import InlineBanner from '@/components/inline-banner'
import PageSubtitle from '@/components/page-subtitle'
import SuiteInfo from '@/features/Listing/suite-info'
import PaymentCard from '@/features/Payments/payment-card'
import { select } from '@/store'
import { breakpoints, colors, fontSizes, fontWeights } from '@/styles'

const Wrapper = styled.div`
  text-align: center;
  margin-bottom: 100px;
`

const ContentWrapper = styled.div`
  max-width: 620px;
  margin: 40px auto;
  padding: 0 10px;
  text-align: center;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    margin: 40px 16px 80px 16px;
  }
`

const PaymentWrapper = styled.div`
  max-width: 600px;
  margin: 0 auto;
  text-align: left;
  > div {
    margin-bottom: 24px;
  }
`

const PaymentTitle = styled.p`
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.medium};
  line-height: 28px;
  margin: 0;
  margin-bottom: ${props => props.bottom || 0}px;
  margin-top: ${props => props.top || 0}px;
  text-align: ${props => (props.center ? 'center' : 'left')};

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    text-align: center;
  }
`

const EmptyText = styled.p`
  width: 100%;
  max-width: 600px;
  border-radius: 6px;
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06);
  background-color: ${colors.white};
  margin: 0 auto;
  padding: 25px;
`

const BannerWrapper = styled.div`
  > div {
    margin-bottom: 40px;
  }
`

const PaymentsDashboard = ({
  match: {
    params: { listingId }
  },
  history
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const loadPaymentData = dispatch.payment.getListingPayments
  const paymentsData = useSelector(state => select.payment.listingPayments(state, { listingId }))
  const contractId = useSelector(path(['listing', listingId, 'contract', 'listing_contract_id']))
  const contract = useSelector(state => select.contract.contractView(state, { contractId }))
  const isPadActive = contract.pad_active
  const isPadRequested = contract.pad_requested
  const hasInProgressPayments = paymentsData.progress?.length > 0

  useEffect(() => {
    if (contractId) {
      dispatch.contract.load(contractId)
    }
  }, [contractId, dispatch.contract])

  useEffect(() => {
    loadPaymentData({ listingId })
  }, [listingId, loadPaymentData])

  if (!paymentsData) return null

  const handleGoToPayment = (type, paymentId) => {
    if (type === 'pad')
      history.push(`/rental/${listingId}/payments/${paymentId}/pre-authorized-debit`)
    else history.push(`/rental/${listingId}/payments/${paymentId}`)
    window.scrollTo(0, 0)
  }

  return (
    <Wrapper>
      <Helmet>
        <title>{t('b.payments.view.payments.title', 'Payments')}</title>
      </Helmet>
      <PageSubtitle>{t('b.view.payments.link', 'Payments')}</PageSubtitle>
      <SuiteInfo listingId={listingId} />
      <ContentWrapper>
        <PaymentWrapper>
          {isPadActive && (
            <BannerWrapper>
              <InlineBanner
                theme="blue"
                icon="info_blue2"
                space="fit"
                isShow={true}
                body={t(
                  'l.listing.view.automatic_rent_payments.text',
                  'Automatic rental payments have been set up for this listing.'
                )}
                contentAlign="flexStart"
                iconAlign="center"
              />
            </BannerWrapper>
          )}
          <PaymentTitle bottom={24}>
            {t('r.rental_dashboard.edit.upcoming_payments.text', 'Upcoming Payment(s)')}
          </PaymentTitle>
          {paymentsData.pending?.length > 0 ? (
            paymentsData.pending?.map(payment => (
              <PaymentCard
                key={payment.id}
                data={payment}
                onClick={() => handleGoToPayment(payment.request_type, payment.id)}
                isPadActive={isPadActive}
                isPadRequested={isPadRequested}
              />
            ))
          ) : (
            <EmptyText>
              {t('r.rental_dashboard.edit.no_upcoming_payments.text', 'No upcoming payments')}
            </EmptyText>
          )}
        </PaymentWrapper>

        <PaymentWrapper>
          <PaymentTitle bottom={24} top={40}>
            {t('r.rental_dashboard.edit.progress_rent_payments.text', 'In Progress Payment(s)')}
          </PaymentTitle>
          {hasInProgressPayments ? (
            paymentsData.progress?.map(payment => (
              <PaymentCard
                key={payment.id}
                data={payment}
                onClick={() => handleGoToPayment(payment.request_type, payment.id)}
                isPadActive={isPadActive}
                isPadRequested={isPadRequested}
              />
            ))
          ) : (
            <EmptyText>
              {t('r.rental_dashboard.edit.no_in_progress_payments.text', 'No in progress payments')}
            </EmptyText>
          )}
        </PaymentWrapper>
        <PaymentWrapper>
          <PaymentTitle bottom={24} top={40}>
            {t('r.rental_dashboard.edit.past_rent_payments.text', 'Past Payment(s)')}
          </PaymentTitle>
          {paymentsData.approved?.length > 0 ? (
            paymentsData.approved?.map(payment => (
              <PaymentCard
                key={payment.id}
                data={payment}
                onClick={() => handleGoToPayment(payment.request_type, payment.id)}
                isPadActive={isPadActive}
                isPadRequested={isPadRequested}
              />
            ))
          ) : (
            <EmptyText>
              {t('r.rental_dashboard.edit.no_past_payments.text', 'No past payments')}
            </EmptyText>
          )}
        </PaymentWrapper>
      </ContentWrapper>
    </Wrapper>
  )
}

export default PaymentsDashboard
